import React from "react";

export type WorkflowCapture = {
  type?: string;
  parameter?: string;
  units?: string;
  expectedLow?: string | number | null;
  expectedHigh?: string | number | null;
  target?: string | number | null;
  tolerance?: string | number | null;
  method?: string;
  notes?: string;
};

export type WorkflowPreparationItemType = "PART" | "KIT" | string;

export type WorkflowPreparationItem = {
  id?: string;
  catalogType?: WorkflowPreparationItemType;
  partNumber?: string;
  kitNumber?: string;
  sku?: string;
  description?: string;
  reason?: string;
  source?: string;
  catalogId?: string;
  name?: string;
};

export type WorkflowPreparation = {
  requiredParts?: WorkflowPreparationItem[];
  parts?: Array<unknown>;
  specialTools?: string[];
};

export type WorkflowChecklistItem = {
  id?: string;
  label: string;
  spec?: string | null;
  capture?: WorkflowCapture | null;
  sourceNodeIds?: string[];
  sourceDocumentRefs?: string[];
};

export type WorkflowStepMetadata = {
  layout?: string;
  checklist?: WorkflowChecklistItem[];
  tools?: string[];
  parts?: string[];
  warnings?: string[];
  estimatedTimeMinutes?: number | null;
  sourceNodeIds?: string[];
  sourceDocumentRefs?: string[];
  [key: string]: any;
};

export type WorkflowStep = {
  stepKey?: string | null;
  id?: string;
  order?: number;
  label: string;
  title?: string;
  metadata?: WorkflowStepMetadata;
  [key: string]: any;
};

export type WorkflowJsonDraft = {
  schemaVersion?: number;
  format?: string;
  title?: string;
  category?: string;
  description?: string;
  purpose?: string;
  estimatedDurationMinutes?: number | null;
  estimatedTimeMinutes?: number | null;
  preparation?: WorkflowPreparation;
  prerequisites?: string[];
  tools?: string[];
  parts?: string[];
  safetyNotes?: string[];
  openQuestions?: string[];
  citations?: any[];
  source?: Record<string, any>;
  knowledgeContext?: Record<string, any>;
  steps: WorkflowStep[];
  [key: string]: any;
};

const asText = (value: unknown) => (typeof value === "string" ? value.trim() : "");

const cleanWorkflowText = (value: unknown) =>
  asText(value)
    .replace(/\*\*/g, "")
    .replace(/\((?:Node|node)\s+node_[^)]+\)/gi, "")
    .replace(/\bnode_[A-Za-z0-9_-]+\b/g, "")
    .replace(/\((?:see\s+)?(?:knowledge|service|source)\s+nodes?[^)]*\)/gi, "")
    .replace(/\b(?:see\s+)?(?:knowledge|service|source)\s+nodes?\b/gi, "")
    .replace(/\bStep\s+[A-Z0-9_-]+\b/g, "")
    .replace(
      /\b(?:as\s+)?(?:(?:described|confirmed|recorded|completed)\s+)?(?:in|from|during|after|before)?\s*(?:the\s+)?(?:previous|earlier|above)\s+step\b/gi,
      ""
    )
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Za-z])([0-9])/g, "$1 $2")
    .replace(/([0-9])([A-Za-z])/g, "$1 $2")
    .replace(/([.!?,;:)\]])([A-Za-z0-9])/g, "$1 $2")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([,.;:)])/g, "$1")
    .replace(/\(\s*\)/g, "")
    .trim();

const isAdministrativeWorkflowText = (value: unknown) => {
  const source = cleanWorkflowText(value).toLowerCase();
  if (!source) return false;
  return [
    /\bescalat(e|ion)\b/,
    /\bcontact\s+(?:foss\s+)?support\b/,
    /\btechnical support\b/,
    /\bif unable\b/,
    /\bunable to proceed\b/,
    /\binform (?:the )?customer\b/,
    /\bnotify (?:the )?customer\b/,
    /\blog (?:a )?(?:ticket|case)\b/,
    /\bschedule (?:a )?(?:follow[- ]?up|return visit)\b/,
    /\bupdate (?:the )?(?:service report|case|ticket)\b/,
    /\bclose (?:the )?(?:(?:service|site)\s+)?(?:job|case|task|workflow)\b/,
    /\bcomplete (?:the )?(?:job|case|task|workflow|service report)\b/,
    /\bdocument (?:the )?(?:results?|completion|outcome|work|steps?)\b/,
    /\bdocumentation\b/,
    /\bdocument (?:all )?(?:replaced|removed|installed|changed) parts?\b/,
    /\brecord (?:the )?(?:service|job|case) (?:measurements?|readings?|serial numbers?|data|details)\b/,
    /\brecord .*?\bin (?:the )?(?:service report|case|ticket)\b/,
    /\brecord (?:the )?(?:installed|removed|replaced|changed) (?:kit|kits|parts?|components?)\b/,
    /\brecord (?:the )?(?:kit|part|component).*\b(?:warranty|return|returns?|disposal|registration)\b/,
    /\brecord (?:the )?(?:results?|completion|outcome) in (?:the )?(?:service report|case|ticket)\b/,
    /\b(?:service|case|job) record\b/,
    /\battach (?:photos?|images?|evidence)\b/,
    /\bcapture (?:photos?|images?|evidence)\b/,
    /\bphoto(?:graph)? (?:the )?(?:installed|removed|completed|final)\b/,
    /\bnote (?:any )?(?:outstanding|follow[- ]?up) (?:items?|actions?|parts?)\b/,
    /\bfollow (?:site|supplier|local|manufacturer) (?:policy|policies|instructions?)\b/,
    /\breturn or disposal\b/
  ].some(pattern => pattern.test(source));
};

const isUnresolvedProcedureReferenceText = (value: unknown) => {
  const source = cleanWorkflowText(value).toLowerCase();
  if (!source) return false;
  return [
    /\bper\s+(?=[^.]*\b(?:diagnostic|diagnostics|qa|quality assurance|site|local|instrument|service|maintenance|manufacturer|supplier)\b)[^.]{0,90}\b(?:instructions?|procedures?|methods?|manual|guidance)\b/,
    /\bfollow\s+(?=[^.]*\b(?:diagnostic|diagnostics|qa|quality assurance|site|local|instrument|service|maintenance|manufacturer|supplier)\b)[^.]{0,90}\b(?:instructions?|procedures?|methods?|manual|guidance)\b/,
    /\bacceptance criteria (?:depend|depends|not available|missing|unknown)\b/,
    /\bsee openquestions\b/,
    /\bsee open questions\b/
  ].some(pattern => pattern.test(source));
};

const asArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map(item => String(item || "").trim()).filter(Boolean)
    : asText(value)
      ? [asText(value)]
      : [];

const parseInteger = (value: unknown): number | null => {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim()
        ? Number(value.trim())
        : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : null;
};

const clone = <T,>(value: T): T => {
  try {
    return JSON.parse(JSON.stringify(value)) as T;
  } catch {
    return value;
  }
};

const normalizeWorkflowPreparationItemType = (value: unknown): "KIT" | "PART" => {
  const rawType = asText(value).toUpperCase();
  return rawType === "KIT" ? "KIT" : "PART";
};

const normalizePreparationRequiredPart = (value: unknown): WorkflowPreparationItem | null => {
  if (typeof value === "string") {
    const partNumber = asText(value);
    if (!partNumber) return null;
    return { catalogType: "PART", partNumber, description: "", reason: "" };
  }

  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const catalogType = normalizeWorkflowPreparationItemType(record.catalogType || record.type);
  const partNumber = asText(record.partNumber || record.kitNumber || record.sku || record.number);
  const description = cleanWorkflowText(record.description || record.name || record.title || record.spec);
  const reason = cleanWorkflowText(record.reason || record.notes || record.note);
  const catalogId = asText(record.catalogId || record.id);
  const source = asText(record.source || record.sourceType);
  if (!catalogType && !partNumber && !description && !catalogId) return null;
  return {
    catalogType,
    partNumber,
    kitNumber: asText(record.kitNumber),
    sku: asText(record.sku),
    description,
    reason,
    source: source || (catalogId ? "CATALOG" : undefined),
    catalogId: catalogId || undefined,
    name: asText(record.name)
  };
};

const normalizePreparationRequiredParts = (value: unknown): WorkflowPreparationItem[] => {
  if (!Array.isArray(value)) return [];
  const normalized = value
    .map((item, index) => {
      const normalizedItem = normalizePreparationRequiredPart(item);
      if (!normalizedItem) return null;
      return {
        ...normalizedItem,
        id: asText((normalizedItem as any).id) || `part-${index}`,
        partNumber: normalizedItem.partNumber || asText((normalizedItem as any).sku),
        description: normalizedItem.description || asText((normalizedItem as any).name)
      };
    })
    .filter(Boolean) as WorkflowPreparationItem[];
  const seen = new Set<string>();
  return normalized.filter(item => {
    const keySource = asText(item?.catalogId || item?.partNumber || item?.sku || item?.kitNumber || item?.name || item?.description);
    const key = `${String(item.catalogType || "PART").toUpperCase()}:${keySource.toUpperCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const normalizeWorkflowPreparation = (value: unknown, defaults: WorkflowPreparation = {}): WorkflowPreparation => {
  const preparation = value && typeof value === "object" ? (value as WorkflowPreparation) : {};
  const defaultsList = defaults && typeof defaults === "object" ? defaults : {};
  const rawRequiredParts = Array.isArray(preparation.requiredParts)
    ? preparation.requiredParts
    : Array.isArray(preparation.parts)
      ? preparation.parts
      : Array.isArray(defaultsList.requiredParts)
        ? defaultsList.requiredParts
        : [];
  const requiredParts = normalizePreparationRequiredParts(rawRequiredParts);
  const specialTools = asArray(preparation.specialTools || defaultsList.specialTools);
  return {
    ...(defaultsList || {}),
    ...(preparation || {}),
    requiredParts,
    specialTools,
    parts: []
  };
};

const firstWorkflowArray = (...candidates: unknown[]): unknown[] | undefined =>
  candidates.find(candidate => Array.isArray(candidate)) as unknown[] | undefined;

const extractWorkflowChecklistCollection = (step: any = {}, metadata: any = {}) => {
  const content = step?.content && typeof step.content === "object" ? step.content : {};
  return firstWorkflowArray(
    metadata.checklist,
    step?.checklist,
    step?.checks,
    step?.workflowChecks,
    step?.acceptanceChecks,
    step?.items,
    step?.actions,
    step?.tasks,
    step?.bullets,
    step?.instructions,
    step?.substeps,
    step?.entries,
    step?.workflowItems,
    step?.checklistItems,
    content.checklist,
    content.items,
    content.actions,
    content.tasks
  );
};

const extractWorkflowSectionLabel = (step: any = {}) =>
  cleanWorkflowText(
    step?.label ||
      step?.title ||
      step?.sectionLabel ||
      step?.section ||
      step?.heading ||
      step?.name ||
      step?.phase ||
      step?.topic ||
      step?.l
  );

const extractWorkflowStepInstruction = (step: any = {}) =>
  cleanWorkflowText(
    step?.instruction ||
      step?.text ||
      step?.description ||
      step?.body ||
      step?.summary ||
      step?.action ||
      step?.task ||
      step?.work ||
      step?.s
  );

const isBinaryConfirmationCapture = (value: any): boolean => {
  if (!value || typeof value !== "object") return false;
  const text = [
    value.type,
    value.kind,
    value.parameter,
    value.name,
    value.label,
    value.units,
    value.unit,
    value.target,
    value.expectedLow,
    value.expectedHigh,
    value.tolerance,
    value.method,
    value.notes,
    value.note
  ]
    .map(item => String(item ?? "").trim().toLowerCase())
    .filter(Boolean)
    .join(" ");
  return /\b(pass\s*\/\s*fail|pass[- ]?fail|yes\s*\/\s*no|true\s*\/\s*false|ok\s*\/\s*not\s*ok|complete\s*\/\s*incomplete|go\s*\/\s*no[- ]?go|confirmation|checkbox|boolean)\b/i.test(text);
};

const isLikelySettingCapture = (value: any, contextText = ""): boolean => {
  const source = cleanWorkflowText(
    `${contextText} ${value?.type || ""} ${value?.kind || ""} ${value?.parameter || ""} ${value?.name || ""} ${value?.label || ""} ${value?.field || ""} ${value?.prompt || ""} ${value?.method || ""} ${value?.notes || ""} ${value?.note || ""}`
  )
    .toLowerCase()
    .replace(/[\u2022\u00b7*]/g, " ")
    .trim();
  if (!source) return false;
  const hasSettingVerb =
    /\b(?:setting|set(?:ting)?|set\s+to|adjust(?:ed|ing)?|configure|configuration|preset|position|align|calibrate|calibration|program|tune|target|apply|select)\b/i.test(
      source
    );
  if (!hasSettingVerb) return false;
  const hasNumericTarget = /\b(?:to|at|=|:)\s*[-+]?(?:\d+(?:\.\d+)?|\.\d+)\s*[a-zA-Z%\u00b0/._\-\u00b5]*\d*\b/i.test(source);
  if (!hasNumericTarget) return false;
  return true;
};

const isDisallowedWorkflowCaptureText = (value: unknown): boolean => {
  const source = cleanWorkflowText(value).toLowerCase();
  if (!source) return false;
  return [
    /\bpart\s*number\b/,
    /\bp\/n\b/,
    /\bpn\b/,
    /\bsku\b/,
    /\bkit\s*number\b/,
    /\bservice\s*kit\b/,
    /\bmodification\s*kit\b/,
    /\breplacement\s*kit\b/,
    /\brequired\s*parts?\b/,
    /\bparts?\s+to\s+(?:order|take|bring)\b/,
    /\border(?:ed|ing)?\b/,
    /\bprocurement\b/,
    /\bcatalog(?:ue)?\b/,
    /\bavailability\b/,
    /\bsupplier\b/,
    /\bdelivery\b/,
    /\bspares?\b/,
    /\bstamp(?:ed|ing)?\b/,
    /\bidentifier\b/,
    /\bserial\b/,
    /\bbarcode\b/,
    /\bmodel\s*identifier\b/,
    /\bsoftware\s*version\b/,
    /\bversion\s*string\b/,
    /\bpump\s*head\b/,
    /\bhead\s*stamp\b/
  ].some(pattern => pattern.test(source));
};

const isDisallowedWorkflowChecklistText = (value: unknown): boolean => {
  const source = cleanWorkflowText(value).toLowerCase();
  if (!source) return false;
  return [
    /\bpart\s*number\b/,
    /\bp\/n\b/,
    /\bpn\b/,
    /\bsku\b/,
    /\bkit\s*number\b/,
    /\bservice\s*kit\b/,
    /\bmodification\s*kit\b/,
    /\breplacement\s*kit\b/,
    /\brequired\s*parts?\b/,
    /\bparts?\s+to\s+(?:order|request|bring|take|purchase)\b/,
    /\bprocurement\b/,
    /\bcatalog(?:ue)?\b/,
    /\bavailability\b/,
    /\bsupplier\b/,
    /\bdelivery\b/,
    /\bspares?\b/,
    /\bmodel\s*identifier\b/,
    /\bsoftware\s*version\b/,
    /\bversion\s*string\b/,
    /\bserial\b/,
    /\bbarcode\b/
  ].some(pattern => pattern.test(source));
};

const isQuantitativeWorkflowCapture = (value: any, contextText = ""): boolean => {
  const combinedText = cleanWorkflowText(contextText).toLowerCase();
  if (isLikelySettingCapture(value, contextText)) return false;
  const hasStructuredMeasurementMetadata = [
    value?.expectedLow,
    value?.expectedHigh,
    value?.target,
    value?.tolerance,
    value?.min,
    value?.max,
    value?.minimum,
    value?.maximum,
    value?.low,
    value?.high
  ].some(item => item !== undefined && item !== null && String(item).trim() !== "");
  const units = asText(value?.units || value?.unit);
  if (units) return true;
  if (hasStructuredMeasurementMetadata) return true;
  return [
    /\bmeasur(?:e|ed|ement)\b/,
    /\breading\b/,
    /\bresult\b/,
    /\bvalue\b/,
    /\bsignal\b/,
    /\bblank\b/,
    /\bcarry[- ]?over\b/,
    /\bpercent\b/,
    /\bmean\b/,
    /\bwidth\b/,
    /\bvoltage\b/,
    /\bcurrent\b/,
    /\bpressure\b/,
    /\btemperature\b/,
    /\btorque\b/,
    /\bflow\b/,
    /\bcount\b/,
    /\bvac\b/,
    /\bvdc\b/,
    /\bau\b/,
    /\b%\b/
  ].some(pattern => pattern.test(combinedText));
};

const normalizeCapture = (value: any, contextText = ""): WorkflowCapture | null => {
  if (!value || typeof value !== "object") return null;
  if (isBinaryConfirmationCapture(value)) return null;
  if (isLikelySettingCapture(value, contextText)) return null;
  const combinedCaptureText = [
    contextText,
    value.type,
    value.kind,
    value.valueType,
    value.dataType,
    value.parameter,
    value.name,
    value.label,
    value.field,
    value.prompt,
    value.units,
    value.unit,
    value.method,
    value.notes,
    value.note
  ]
    .map(item => asText(item))
    .filter(Boolean)
    .join(" ");
  if (isDisallowedWorkflowCaptureText(combinedCaptureText)) return null;
  if (!isQuantitativeWorkflowCapture(value, combinedCaptureText)) return null;
  const capture: WorkflowCapture = {
    type: asText(value.type || value.kind || value.valueType || value.dataType).toUpperCase() || "MEASUREMENT",
    parameter: asText(value.parameter || value.name || value.label || value.field || value.prompt),
    units: asText(value.units || value.unit),
    expectedLow: value.expectedLow ?? value.min ?? value.minimum ?? value.low ?? null,
    expectedHigh: value.expectedHigh ?? value.max ?? value.maximum ?? value.high ?? null,
    target: value.target ?? value.expected ?? value.nominal ?? null,
    tolerance: value.tolerance ?? value.tol ?? value.plusMinus ?? null,
    method: asText(value.method),
    notes: asText(value.notes || value.note)
  };
  return capture;
};

const normalizeChecklistItems = (value: unknown): WorkflowChecklistItem[] => {
  const rawItems = Array.isArray(value) ? value : [];
  return rawItems
    .map((item: any, index) => {
      if (typeof item === "string") {
        const label = cleanWorkflowText(item);
        if (isAdministrativeWorkflowText(label)) return null;
        if (isUnresolvedProcedureReferenceText(label)) return null;
        return label ? { id: `item-${index + 1}`, label, spec: null } : null;
      }
      if (!item || typeof item !== "object") return null;
      const label = cleanWorkflowText(
        item.label ||
          item.title ||
          item.name ||
          item.action ||
          item.task ||
          item.item ||
          item.description ||
          item.instruction ||
          item.text ||
          item.l ||
          (typeof item.s === "string" && !item.l ? item.s : "")
      );
      const spec = cleanWorkflowText(
        item.spec ||
          item.detail ||
          item.supportingDetail ||
          item.notes ||
          item.note ||
          item.details ||
          item.support ||
          item.reason ||
          item.rationale ||
          item.summary ||
          (item.l ? item.s : "")
      );
      if (!label && !spec) return null;
      if (isDisallowedWorkflowChecklistText(`${label} ${spec}`)) return null;
      if (isAdministrativeWorkflowText(`${label} ${spec}`)) return null;
      if (isUnresolvedProcedureReferenceText(`${label} ${spec}`)) return null;
      const capture = normalizeCapture(item.capture || item.measurement, `${label} ${spec}`);
      return {
        id: asText(item.id) || `item-${index + 1}`,
        label: label || spec,
        spec: spec || null,
        ...(capture ? { capture } : {}),
        sourceNodeIds: asArray(item.sourceNodeIds || item.snippetIds),
        sourceDocumentRefs: asArray(item.sourceDocumentRefs || item.sourceRefs)
      };
    })
    .filter(Boolean) as WorkflowChecklistItem[];
};

export const getWorkflowStepChecklist = (step: WorkflowStep | any): WorkflowChecklistItem[] =>
  normalizeChecklistItems(extractWorkflowChecklistCollection(step, step?.metadata || {}));

export const getWorkflowStepTools = (step: WorkflowStep | any): string[] =>
  asArray(step?.metadata?.tools || step?.tools);

export const getWorkflowStepParts = (step: WorkflowStep | any): string[] =>
  asArray(step?.metadata?.parts || step?.parts);

export const getWorkflowStepWarnings = (step: WorkflowStep | any): string[] =>
  asArray(step?.metadata?.warnings || step?.warnings);

export const getWorkflowStepEstimate = (step: WorkflowStep | any): number | null =>
  parseInteger(step?.metadata?.estimatedTimeMinutes ?? step?.estimatedTimeMinutes ?? step?.estimatedMinutes);

export const createEmptyWorkflowChecklistItem = (): WorkflowChecklistItem => ({
  id: `item-${Math.random().toString(36).slice(2, 10)}`,
  label: "",
  spec: ""
});

export const createEmptyWorkflowStep = (index = 0): WorkflowStep => ({
  stepKey: `step-${index + 1}`,
  order: index + 1,
  label: `Step ${index + 1}`,
  metadata: {
    layout: "WORKFLOW",
    checklist: [createEmptyWorkflowChecklistItem()]
  }
});

export const normalizeWorkflowSteps = (value: unknown): WorkflowStep[] => {
  const rawSteps = Array.isArray(value) ? value : [];
  return rawSteps
    .map((step: any, index) => {
      const metadata = step?.metadata && typeof step.metadata === "object" ? clone(step.metadata) : {};
      const instruction = extractWorkflowStepInstruction(step);
      const notes = cleanWorkflowText(step?.notes || step?.spec || step?.supportingDetail);
      const rawChecklist = extractWorkflowChecklistCollection(step, metadata);
      const hasEditableChecklist = Array.isArray(rawChecklist);
      const checklist = normalizeChecklistItems(
        rawChecklist
      );

      if (instruction) {
        checklist.unshift({
          id: "work",
          label: instruction,
          spec: notes || null
        });
      }
      if (checklist.length === 0 && typeof step === "string" && asText(step)) {
        const label = cleanWorkflowText(step);
        if (label && !isAdministrativeWorkflowText(label)) {
          checklist.push({ id: "work", label, spec: null });
        }
      }
      if (checklist.length === 0 && hasEditableChecklist) {
        const firstItem = Array.isArray(rawChecklist) ? rawChecklist[0] : null;
        checklist.push({
          id: asText(firstItem?.id) || `item-${index + 1}`,
          label: "",
          spec: ""
        });
      }
      if (checklist.length === 0) return null;

      const cleanMetadata = { ...metadata };
      delete cleanMetadata.tools;
      delete cleanMetadata.parts;
      delete cleanMetadata.warnings;
      delete cleanMetadata.estimatedTimeMinutes;
      delete cleanMetadata.estimatedMinutes;
      const sectionLabel = extractWorkflowSectionLabel(step);
      return {
        stepKey: asText(step?.stepKey || step?.id) || `step-${index + 1}`,
        order: index + 1,
        label:
          sectionLabel && !isAdministrativeWorkflowText(sectionLabel) && !isUnresolvedProcedureReferenceText(sectionLabel)
            ? sectionLabel
            : cleanWorkflowText(checklist[0]?.label) || `Step ${index + 1}`,
        metadata: {
          ...cleanMetadata,
          layout: asText(cleanMetadata.layout) || "WORKFLOW",
          checklist,
          sourceNodeIds: asArray(metadata.sourceNodeIds || step?.sourceNodeIds || step?.snippetIds),
          sourceDocumentRefs: asArray(metadata.sourceDocumentRefs || step?.sourceDocumentRefs || step?.sourceRefs)
        }
      };
    })
    .filter(Boolean) as WorkflowStep[];
};

export const normalizeWorkflowJson = (
  value: any,
  defaults: Partial<WorkflowJsonDraft> = {}
): WorkflowJsonDraft => {
  const supplied = value && typeof value === "object" ? value : {};
  const estimated =
    parseInteger(defaults.estimatedDurationMinutes ?? supplied.estimatedDurationMinutes) ??
    parseInteger(defaults.estimatedTimeMinutes ?? supplied.estimatedTimeMinutes);
  const steps = normalizeWorkflowSteps(supplied.steps || defaults.steps);
  return {
    ...supplied,
    schemaVersion: 2,
    format: "PROTOCOL_CHECKLIST",
    title: cleanWorkflowText(defaults.title) || cleanWorkflowText(supplied.title),
    category: asText(defaults.category) || asText(supplied.category) || "SAFETY_UPGRADE",
    description: cleanWorkflowText(defaults.description) || cleanWorkflowText(supplied.description),
    estimatedDurationMinutes: estimated,
    estimatedTimeMinutes: estimated,
    prerequisites: [],
    tools: [],
    parts: [],
    safetyNotes: [],
    preparation: normalizeWorkflowPreparation(supplied.preparation, (defaults as WorkflowJsonDraft).preparation),
    steps
  };
};

export const createEmptyWorkflowJson = (
  defaults: Partial<WorkflowJsonDraft> = {}
): WorkflowJsonDraft =>
  normalizeWorkflowJson(
    {
      schemaVersion: 2,
      format: "PROTOCOL_CHECKLIST",
      category: defaults.category || "SAFETY_UPGRADE",
      source: defaults.source || { type: "MANUAL", id: null, label: "Manual workflow" },
      steps: [createEmptyWorkflowStep(0)]
    },
    defaults
  );

export const workflowHasExecutableContent = (workflowJson: WorkflowJsonDraft | any): boolean =>
  normalizeWorkflowJson(workflowJson).steps.some(step =>
    getWorkflowStepChecklist(step).some(item => asText(item.label) || asText(item.spec || ""))
  );

type WorkflowProtocolEditorProps = {
  value: WorkflowJsonDraft;
  onChange: (next: WorkflowJsonDraft) => void;
  disabled?: boolean;
};

export const WorkflowProtocolEditor: React.FC<WorkflowProtocolEditorProps> = ({
  value,
  onChange,
  disabled = false
}) => {
  const workflow = normalizeWorkflowJson(value);
  const steps = workflow.steps || [];
  const preparation = workflow.preparation || {};
  const requiredParts = Array.isArray(preparation.requiredParts) ? preparation.requiredParts : [];

  const updatePreparation = (patch: Partial<WorkflowPreparation>) => {
    onChange(
      normalizeWorkflowJson({
        ...workflow,
        preparation: normalizeWorkflowPreparation(
          {
            ...(preparation || {}),
            ...patch
          },
          workflow.preparation || {}
        )
      })
    );
  };

  const addPreparationPart = () => {
    updatePreparation({
      requiredParts: [
        ...requiredParts,
        {
          id: `part-${Date.now()}`,
          catalogType: "PART",
          partNumber: "",
          description: "",
          reason: "",
          source: "MANUAL"
        }
      ]
    });
  };

  const updatePreparationPart = (index: number, patch: Partial<WorkflowPreparationItem>) => {
    const nextRequiredParts = requiredParts.map((item, itemIndex) =>
      itemIndex === index
        ? normalizePreparationRequiredPart({ ...(item || {}), ...(patch || {}) }) || item
        : item
    );
    updatePreparation({
      requiredParts: nextRequiredParts
    });
  };

  const removePreparationPart = (index: number) => {
    updatePreparation({
      requiredParts: requiredParts.filter((_item, itemIndex) => itemIndex !== index)
    });
  };

  const updateWorkflow = (patch: Partial<WorkflowJsonDraft>) => {
    onChange(normalizeWorkflowJson({ ...workflow, ...patch }));
  };

  const updateStep = (index: number, patch: Partial<WorkflowStep>) => {
    const nextSteps = steps.map((step, stepIndex) =>
      stepIndex === index
        ? normalizeWorkflowSteps([{ ...step, ...patch }])[0] || step
        : step
    );
    updateWorkflow({ steps: nextSteps });
  };

  const updateStepMetadata = (index: number, patch: Partial<WorkflowStepMetadata>) => {
    const step = steps[index] || createEmptyWorkflowStep(index);
    updateStep(index, {
      metadata: {
        ...(step.metadata || {}),
        ...patch
      }
    });
  };

  const addStep = () => {
    updateWorkflow({ steps: [...steps, createEmptyWorkflowStep(steps.length)] });
  };

  const removeStep = (index: number) => {
    updateWorkflow({
      steps: steps
        .filter((_step, stepIndex) => stepIndex !== index)
        .map((step, stepIndex) => ({ ...step, order: stepIndex + 1, stepKey: step.stepKey || `step-${stepIndex + 1}` }))
    });
  };

  const addChecklistItem = (stepIndex: number) => {
    const step = steps[stepIndex] || createEmptyWorkflowStep(stepIndex);
    const checklist = getWorkflowStepChecklist(step);
    updateStepMetadata(stepIndex, {
      checklist: [...checklist, createEmptyWorkflowChecklistItem()]
    });
  };

  const updateChecklistItem = (
    stepIndex: number,
    itemIndex: number,
    patch: Partial<WorkflowChecklistItem>
  ) => {
    const step = steps[stepIndex] || createEmptyWorkflowStep(stepIndex);
    const checklist = getWorkflowStepChecklist(step);
    updateStepMetadata(stepIndex, {
      checklist: checklist.map((item, index) => (index === itemIndex ? { ...item, ...patch } : item))
    });
  };

  const removeChecklistItem = (stepIndex: number, itemIndex: number) => {
    const step = steps[stepIndex] || createEmptyWorkflowStep(stepIndex);
    const checklist = getWorkflowStepChecklist(step);
    const nextChecklist = checklist.filter((_item, index) => index !== itemIndex);
    updateStepMetadata(stepIndex, {
      checklist: nextChecklist.length > 0 ? nextChecklist : [createEmptyWorkflowChecklistItem()]
    });
  };

  const toggleMeasurement = (stepIndex: number, itemIndex: number) => {
    const item = getWorkflowStepChecklist(steps[stepIndex])[itemIndex];
    updateChecklistItem(stepIndex, itemIndex, {
      capture: item?.capture ? null : { type: "MEASUREMENT", parameter: item?.label || "" }
    });
  };

  const updateCaptureField = (
    stepIndex: number,
    itemIndex: number,
    field: keyof WorkflowCapture,
    fieldValue: string
  ) => {
    const item = getWorkflowStepChecklist(steps[stepIndex])[itemIndex];
    const capture = item?.capture && typeof item.capture === "object" ? item.capture : { type: "MEASUREMENT" };
    updateChecklistItem(stepIndex, itemIndex, {
      capture: {
        ...capture,
        type: "MEASUREMENT",
        [field]: fieldValue
      }
    });
  };

  return (
    <div style={editorStyles.shell}>
      <div style={editorStyles.grid}>
        <label style={editorStyles.field}>
          Estimated minutes
          <input
            type="number"
            min={1}
            style={editorStyles.input}
            value={workflow.estimatedDurationMinutes || ""}
            onChange={event => {
              const estimated = parseInteger(event.target.value);
              updateWorkflow({ estimatedDurationMinutes: estimated, estimatedTimeMinutes: estimated });
            }}
            disabled={disabled}
          />
        </label>
      </div>

      <div style={editorStyles.sectionHeader}>
        <div style={editorStyles.sectionTitle}>Required parts and kits</div>
        <button type="button" style={editorStyles.button} onClick={addPreparationPart} disabled={disabled}>
          Add required part / kit
        </button>
      </div>
      <div style={editorStyles.prepCards}>
        {requiredParts.length > 0 ? (
          requiredParts.map((item, itemIndex) => {
            const catalogType = normalizeWorkflowPreparationItemType(item?.catalogType);
            return (
              <div key={item?.id || `${itemIndex}`} style={editorStyles.prepCard}>
                <div style={editorStyles.stepHeader}>
                  <span style={editorStyles.checkTitle}>Item {itemIndex + 1}</span>
                  <button
                    type="button"
                    style={editorStyles.dangerButton}
                    onClick={() => removePreparationPart(itemIndex)}
                    disabled={disabled}
                  >
                    Remove item
                  </button>
                </div>
                <div style={editorStyles.grid}>
                  <label style={editorStyles.field}>
                    Type
                    <select
                      style={editorStyles.input}
                      value={catalogType}
                      onChange={event =>
                        updatePreparationPart(itemIndex, { catalogType: event.target.value as "PART" | "KIT" })
                      }
                      disabled={disabled}
                    >
                      <option value="PART">Part</option>
                      <option value="KIT">Kit</option>
                    </select>
                  </label>
                  <label style={editorStyles.field}>
                    {catalogType === "KIT" ? "Kit number" : "Part number"}
                    <input
                      style={editorStyles.input}
                      value={item?.partNumber || ""}
                      onChange={event => updatePreparationPart(itemIndex, { partNumber: event.target.value })}
                      disabled={disabled}
                    />
                  </label>
                </div>
                <label style={editorStyles.fieldWide}>
                  Description
                  <input
                    style={editorStyles.input}
                    value={item?.description || ""}
                    onChange={event => updatePreparationPart(itemIndex, { description: event.target.value })}
                    disabled={disabled}
                  />
                </label>
                <label style={editorStyles.fieldWide}>
                  Why required
                  <textarea
                    style={editorStyles.textarea}
                    value={item?.reason || ""}
                    onChange={event => updatePreparationPart(itemIndex, { reason: event.target.value })}
                    disabled={disabled}
                  />
                </label>
              </div>
            );
          })
        ) : (
          <div style={editorStyles.emptyState}>No required parts or kits selected.</div>
        )}
      </div>

      <div style={editorStyles.sectionHeader}>
        <div style={editorStyles.sectionTitle}>Workflow sections</div>
        <button type="button" style={editorStyles.button} onClick={addStep} disabled={disabled}>
          Add section
        </button>
      </div>

      <div style={editorStyles.steps}>
        {steps.map((step, stepIndex) => {
          const checklist = getWorkflowStepChecklist(step);
          return (
            <div key={step.stepKey || stepIndex} style={editorStyles.stepCard}>
              <div style={editorStyles.stepHeader}>
                <div style={editorStyles.stepNumber}>{stepIndex + 1}</div>
                <button
                  type="button"
                  style={editorStyles.dangerButton}
                  onClick={() => removeStep(stepIndex)}
                  disabled={disabled || steps.length <= 1}
                >
                  Remove section
                </button>
              </div>
              <div style={editorStyles.grid}>
                <label style={editorStyles.fieldWide}>
                  Section title
                  <input
                    style={editorStyles.input}
                    value={step.label || ""}
                    onChange={event => updateStep(stepIndex, { label: event.target.value })}
                    disabled={disabled}
                  />
                </label>
              </div>

              <div style={editorStyles.checkHeader}>
                <div style={editorStyles.checkTitle}>Checklist items</div>
                <button type="button" style={editorStyles.button} onClick={() => addChecklistItem(stepIndex)} disabled={disabled}>
                  Add checklist item
                </button>
              </div>
              <div style={editorStyles.checks}>
                {checklist.map((item, itemIndex) => {
                  const capture = item.capture && typeof item.capture === "object" ? item.capture : null;
                  return (
                    <div key={item.id || itemIndex} style={editorStyles.checkCard}>
                      <label style={editorStyles.field}>
                        Checklist label
                        <input
                          style={editorStyles.input}
                          value={item.label || ""}
                          onChange={event =>
                            updateChecklistItem(stepIndex, itemIndex, { label: event.target.value })
                          }
                          disabled={disabled}
                        />
                      </label>
                      <label style={editorStyles.field}>
                        Supporting detail / spec
                        <textarea
                          style={{ ...editorStyles.textarea, minHeight: 88 }}
                          value={item.spec || ""}
                          onChange={event =>
                            updateChecklistItem(stepIndex, itemIndex, { spec: event.target.value })
                          }
                          disabled={disabled}
                        />
                      </label>
                      <div style={editorStyles.row}>
                        <button
                          type="button"
                          style={editorStyles.button}
                          onClick={() => toggleMeasurement(stepIndex, itemIndex)}
                          disabled={disabled}
                        >
                          {capture ? "Remove measurement" : "Add measurement"}
                        </button>
                        <button
                          type="button"
                          style={editorStyles.dangerButton}
                          onClick={() => removeChecklistItem(stepIndex, itemIndex)}
                          disabled={disabled}
                        >
                          Remove item
                        </button>
                      </div>
                      {capture ? (
                        <div style={editorStyles.captureGrid}>
                          {(["parameter", "units", "expectedLow", "expectedHigh", "target", "tolerance", "method"] as const).map(field => (
                            <label key={field} style={editorStyles.field}>
                              {field}
                              <input
                                style={editorStyles.input}
                                value={String(capture[field] ?? "")}
                                onChange={event =>
                                  updateCaptureField(stepIndex, itemIndex, field, event.target.value)
                                }
                                disabled={disabled}
                              />
                            </label>
                          ))}
                          <label style={editorStyles.fieldWide}>
                            Measurement notes
                            <textarea
                              style={editorStyles.textarea}
                              value={capture.notes || ""}
                              onChange={event =>
                                updateCaptureField(stepIndex, itemIndex, "notes", event.target.value)
                              }
                              disabled={disabled}
                            />
                          </label>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const editorStyles: Record<string, React.CSSProperties> = {
  shell: {
    display: "grid",
    gap: "1rem"
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "0.75rem"
  },
  field: {
    display: "grid",
    gap: "0.35rem",
    color: "#123F43",
    fontSize: "0.84rem",
    fontWeight: 700,
    minWidth: 0
  },
  fieldWide: {
    display: "grid",
    gap: "0.35rem",
    color: "#123F43",
    fontSize: "0.84rem",
    fontWeight: 700,
    minWidth: 0,
    gridColumn: "1 / -1"
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #DDD4C8",
    borderRadius: 6,
    padding: "0.58rem 0.65rem",
    background: "#FFFDF9",
    color: "#123F43",
    font: "inherit",
    fontWeight: 500
  },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #DDD4C8",
    borderRadius: 6,
    padding: "0.58rem 0.65rem",
    background: "#FFFDF9",
    color: "#123F43",
    font: "inherit",
    fontWeight: 500,
    minHeight: 70,
    resize: "vertical"
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.75rem",
    flexWrap: "wrap"
  },
  sectionTitle: {
    color: "#123F43",
    fontWeight: 800,
    fontSize: "1rem"
  },
  button: {
    border: "1px solid #DDD4C8",
    background: "#FFFDF9",
    color: "#123F43",
    borderRadius: 6,
    padding: "0.5rem 0.75rem",
    fontWeight: 800,
    cursor: "pointer"
  },
  dangerButton: {
    border: "1px solid #A63B32",
    background: "#FFFDF9",
    color: "#A63B32",
    borderRadius: 6,
    padding: "0.45rem 0.65rem",
    fontWeight: 800,
    cursor: "pointer"
  },
  steps: {
    display: "grid",
    gap: "0.9rem"
  },
  stepCard: {
    border: "1px solid #DDD4C8",
    background: "#FBF8F2",
    borderRadius: 8,
    padding: "0.9rem",
    display: "grid",
    gap: "0.75rem"
  },
  stepHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.75rem"
  },
  stepNumber: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#E7F2EC",
    color: "#123F43",
    fontWeight: 800
  },
  checkHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.75rem",
    flexWrap: "wrap"
  },
  checkTitle: {
    color: "#123F43",
    fontWeight: 800
  },
  checks: {
    display: "grid",
    gap: "0.65rem"
  },
  checkCard: {
    border: "1px solid #DDD4C8",
    background: "#FFFDF9",
    borderRadius: 8,
    padding: "0.75rem",
    display: "grid",
    gap: "0.65rem"
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    gap: "0.75rem",
    alignItems: "center",
    flexWrap: "wrap"
  },
  captureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "0.65rem"
  },
  prepCards: {
    display: "grid",
    gap: "0.75rem"
  },
  prepCard: {
    border: "1px solid #DDD4C8",
    background: "#FFFDF9",
    borderRadius: 8,
    padding: "0.75rem",
    display: "grid",
    gap: "0.65rem"
  },
  emptyState: {
    color: "#667085",
    fontSize: "0.88rem"
  }
};

export default WorkflowProtocolEditor;
